/* eslint-disable @typescript-eslint/naming-convention */
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";

import type { ITableProps } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import {
    useFiltersStateFromParams,
    updateFiltersStateParams,
} from "app/pages/digital-marketing/monitor-and-protect/services/keywords-quick-filters";
import { AlertsModal } from "app/pages/digital-marketing/monitor-and-protect/shared/components/alerts/alerts-modal";
import { InfoTooltip } from "app/pages/digital-marketing/common/ui/InfoTooltip";
import { Widget } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/layout/widget";
import type { SortOrder, SorterResult } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/table/builders";
import { getNoData } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/table/get-no-data";
import { useTranslation } from "pro-features/components/WithTranslation";
import { allTrackers } from "app/services/track/track";

import { mnpNavigation } from "../../../app/navigation";
import { useMonitorAndProtectDataAndParams } from "../../hooks/navigations";
import type { ICompetitorsTableRow } from "../competitors-chart/types";

import { getCompetitorsConfigBuilder } from "./competitors-table-data";
import type { ICompetitorsFilterState } from "./CompetitorsDataFilters";
import { CompetitorsDataFilters } from "./CompetitorsDataFilters";
import { useCompetitorsTable } from "./use-competitors-table";

const TableHighlight = styled.div`
    display: contents;
    .highlight,
    .highlight td.ant-table-column-sort {
        background-color: ${colorsPalettes.blue[50]};
    }
`;

const isEmptyArray = (value: unknown[]): boolean => {
    return Array.isArray(value) && value.length === 0;
};

interface CompetitorsTableProps {
    compact?: boolean;
}
export const CompetitorsTable: FC<CompetitorsTableProps> = ({ compact = false }) => {
    const tableRef = useRef<HTMLDivElement>(null);
    const filterState = useFiltersStateFromParams<ICompetitorsFilterState>();
    const [syncState, setSyncState] = useState<ICompetitorsFilterState>(filterState);
    const table = useCompetitorsTable(syncState);
    const translate = useTranslation();
    const params = useMonitorAndProtectDataAndParams();

    const handleSeeAllRedirect = useCallback(() => {
        void mnpNavigation.competitors.go(params);
    }, [params]);
    const [{ pageSize, current }, setPaginationData] = useState<ITableProps["pagination"]>({
        pageSize: compact ? 6 : 10,
        current: 1,
    });
    const [sortState, setSortState] = useState<{ columnKey: string; order: SortOrder } | null>({
        columnKey: "paidClicks",
        order: "descend",
    });

    const handleSyncState = (newState: ICompetitorsFilterState) => {
        const processedState: ICompetitorsFilterState = { ...newState };

        if (isEmptyArray(processedState.competitors)) {
            processedState.competitors = undefined;
        }

        setSyncState(processedState);
        updateFiltersStateParams(processedState);
    };

    const handleSortChange = useCallback((sorter: SorterResult<ICompetitorsTableRow>) => {
        const newSortState = sorter.order
            ? {
                  columnKey: sorter.columnKey as string,
                  order: sorter.order,
              }
            : null;
        setPaginationData((prev) => ({ ...prev, current: 1 }));
        setSortState(newSortState);
    }, []);

    const tableConfig = useMemo(() => {
        const builder = getCompetitorsConfigBuilder({
            competitorCount: table.filteredData.length,
            pageSize,
            currentPage: current,
            enableSorting: !compact,
        }).setPagination({
            pageSize,
            current,
            total: compact ? pageSize : table.filteredData.length,
            onChange: (_currentPage: number, _pageSize: number, nextPage: number) => {
                setPaginationData((prev) => ({ ...prev, current: nextPage }));
            },
        });

        // Only add sorting in non-compact mode
        if (!compact) {
            builder.setControlledSort(sortState, handleSortChange);
        }

        return builder.build();
    }, [table.filteredData.length, pageSize, current, compact, sortState, handleSortChange]);

    useEffect(() => {
        if (Object.keys(filterState).length > 0) {
            tableRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [filterState]);

    return (
        <Widget>
            <Widget.Header>
                <Flex
                    gap="8px"
                    alignItems="center"
                >
                    <Text
                        fontVariant="h5_2"
                        color={colorsPalettes.carbon[500]}
                    >
                        {translate("monitor_and_protect.overview.competitors_table.title")}
                    </Text>
                    <InfoTooltip content={translate("monitor_and_protect.page.competitors.table.title")} />
                </Flex>
                <AlertsModal />
            </Widget.Header>
            {compact ? null : (
                <Flex
                    padding="16px 24px"
                    tabIndex={0}
                    ref={tableRef}
                    gap="16px"
                >
                    <CompetitorsDataFilters
                        competitorsData={table.rawData}
                        filterState={syncState}
                        onFilterChange={handleSyncState}
                        onFilterClick={(filterName) => {
                            allTrackers.dispatchTrackEvent("Drop Down", "open", filterName, {
                                country: table.countries.join(","),
                            });
                        }}
                        onFilterChangeEvent={(filterName) => {
                            allTrackers.dispatchTrackEvent("Filter Table", "click", filterName, {
                                country: table.countries.join(","),
                            });
                        }}
                    />
                </Flex>
            )}
            <TableHighlight>
                <Table
                    data={table.filteredData}
                    isLoading={table.isLoading}
                    columnConfig={tableConfig.columns}
                    pagination={tableConfig.pagination}
                    onSort={tableConfig.onSort}
                    sticky={{
                        offsetHeader: -32,
                    }}
                    noData={getNoData(table.isError, "competitors", translate)}
                    rowClassName={(e) => (e.domain.isYou ? "highlight" : "")}
                    scroll={{ x: 1000 }}
                    localStorageKey="competitors-report-table-columns"
                />
                {compact && !table.isLoading && table.filteredData.length > 0 ? (
                    <Flex
                        justifyContent="end"
                        padding="8px"
                    >
                        <Button
                            onClick={handleSeeAllRedirect}
                            type="flat"
                        >
                            {translate("monitor_and_protect.page.overview.component.table.action_button")}
                        </Button>
                    </Flex>
                ) : null}
            </TableHighlight>
        </Widget>
    );
};
